import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore, useView } from '../../state/store';
import { generateId } from '../../utils/helpers';
import { PlanViewMaterial, HiddenLineWrapper } from '../../rendering/Materials';
import { updateWallJoins } from '../../utils/WallGeometryUtils';
import { scheduleBackgroundWork } from '../../utils/backgroundScheduler';
import { GROUND_PLANE, snapToGroundGrid } from '../../core/scene';
import { UNIT_BOX_GEOMETRY } from '../../rendering/pooledGeometry';
import {
    AlignmentGuide,
    AlignmentReference,
    collectWallAlignmentReferences,
    resolveAlignment,
} from '../snapping/alignmentGuides';

interface WallPreview {
    start: THREE.Vector3;
    end: THREE.Vector3;
    height: number;
    thickness: number;
}

/**
 * Wall Tool Hook - BIM-style parametric wall creation
 * 
 * Replicates BIM's Wall tool:
 * - Click to start wall
 * - Move to define length
 * - Click to place (continues for chain)
 * - Double-click or Escape to finish
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWallTool() {
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPoint, setStartPoint] = useState<THREE.Vector3 | null>(null);
    const [previewEnd, setPreviewEnd] = useState<THREE.Vector3 | null>(null);
    const [wallHeight, setWallHeight] = useState(3.0); // Default 3m height
    const [wallThickness, setWallThickness] = useState(0.2); // Default 200mm
    // Active alignment previews (convergent snapping): dashed guides shown
    // while the cursor tracks an existing wall endpoint's X/Y alignment.
    const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuide[]>([]);

    const addEntity = useAppStore((s) => s.addEntity);
    const updateEntity = useAppStore((s) => s.updateEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);
    const entities = useAppStore((s) => s.entities);

    const { raycaster, pointer, camera } = useThree();

    const isActive = activeTool === 'wall';

    /**
     * Get point on ground plane from mouse (canonical Z-up frame)
     */
    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    /**
     * Snap to grid (0.5m increments)
     */
    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gridSize = 0.5;
        return snapToGroundGrid(point, gridSize, 0);
    }, []);

    // Alignment references from the placed walls; recomputed only when the
    // entity map changes, never per pointer event.
    const alignmentRefs = useMemo<AlignmentReference[]>(
        () => collectWallAlignmentReferences(entities as Record<string, any>),
        [entities]
    );
    // Dedupe guide writes: identical guide sets skip the state write.
    const lastGuideKeyRef = useRef('');

    /**
     * Resolve a raw ground point through grid snapping plus convergent
     * alignment snapping, updating the guide preview. Used by BOTH the move
     * preview and the click commit so the placed point always equals the
     * last previewed frame (zero pop).
     */
    const resolveDraftPoint = useCallback((raw: THREE.Vector3): THREE.Vector3 => {
        const snapped = snap(raw);
        // Tolerance scales with camera distance (1.2% of it), matching the
        // 3D shape-handle snap feel across zoom levels.
        const tolerance = Math.min(0.6, Math.max(0.03, camera.position.distanceTo(snapped) * 0.012));
        const anchor: AlignmentReference | null = startPoint
            ? { x: startPoint.x, y: startPoint.y, z: startPoint.z }
            : null;
        const result = resolveAlignment(snapped, alignmentRefs, tolerance, anchor);
        const key = result.guides
            .map((g) => `${g.from.join(',')}|${g.to.join(',')}`)
            .join(';');
        if (key !== lastGuideKeyRef.current) {
            lastGuideKeyRef.current = key;
            setAlignmentGuides(result.guides);
        }
        return result.point;
    }, [snap, camera, alignmentRefs, startPoint]);

    const clearAlignmentGuides = useCallback(() => {
        lastGuideKeyRef.current = '';
        setAlignmentGuides([]);
    }, []);

    /**
     * Create wall entity
     */
    const createWall = useCallback((start: THREE.Vector3, end: THREE.Vector3) => {
        const id = generateId();
        const length = start.distanceTo(end);
        // Heading in the XY ground plane (Z-up: yaw about +Z)
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

        // Initial wall data
        const wallData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            center: [center.x, center.y, wallHeight / 2],
            length,
            height: wallHeight,
            thickness: wallThickness,
            angle,
            material: 'concrete',
        };

        addEntity({
            id,
            type: 'wall',
            name: `Wall-${id.slice(0, 6)}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: wallData,
        });

        // Trigger join update — in the background.
        // The join pass walks every entity (O(n)) and each resulting write
        // re-renders the scope; running it inline held both the placement
        // click and an immediate command exit behind that storm. addEntity
        // is synchronous in Zustand, so the deferred task re-reads live
        // state and also picks up any walls placed in between.
        scheduleBackgroundWork(() => {
            const updates = updateWallJoins(useAppStore.getState().entities);

            // Apply updates
            Object.entries(updates).forEach(([entityId, update]) => {
                updateEntity(entityId, update);
            });
        });

        return id;
    }, [addEntity, updateEntity, activeLayerId, wallHeight, wallThickness]);

    /**
     * Handle pointer down - start or continue wall
     */
    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;

        event.stopPropagation();
        const point = getGroundPoint();
        if (!point) return;

        const snapped = resolveDraftPoint(point);

        if (!isDrawing) {
            // Start new wall
            setIsDrawing(true);
            setStartPoint(snapped);
            setPreviewEnd(snapped);
        } else if (startPoint) {
            // Place wall and continue chain
            if (snapped.distanceTo(startPoint) > 0.1) {
                createWall(startPoint, snapped);
            }
            setStartPoint(snapped); // Continue from this point
        }
    }, [isActive, isDrawing, startPoint, getGroundPoint, resolveDraftPoint, createWall]);

    /**
     * Handle pointer move - update preview
     */
    const handlePointerMove = useCallback(() => {
        if (!isActive || !isDrawing) return;

        const point = getGroundPoint();
        if (point) {
            const snapped = resolveDraftPoint(point);
            // Grid-snapped: identical cell reads must not re-render the scope
            // (setState with the previous reference makes React bail out).
            setPreviewEnd((prev) => (prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped));
        }
    }, [isActive, isDrawing, getGroundPoint, resolveDraftPoint]);

    /**
     * Handle double click - finish wall chain
     */
    const handleDoubleClick = useCallback(() => {
        if (!isActive || !isDrawing) return;

        // Finish the current wall chain
        setIsDrawing(false);
        setStartPoint(null);
        setPreviewEnd(null);
        clearAlignmentGuides();
    }, [isActive, isDrawing, clearAlignmentGuides]);

    /**
     * Handle Escape - cancel
     */
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isDrawing) {
                setIsDrawing(false);
                setStartPoint(null);
                setPreviewEnd(null);
                clearAlignmentGuides();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDrawing, clearAlignmentGuides]);

    return {
        isActive,
        isDrawing,
        startPoint,
        previewEnd,
        wallHeight,
        wallThickness,
        setWallHeight,
        setWallThickness,
        alignmentGuides,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Alignment guide overlay - dashed preview lines for the active convergent
 * alignments while drawing (the plan-drawing analogue of the 3D height
 * alignment guides on the wall shape handles).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function WallAlignmentGuides({ guides }: { guides: AlignmentGuide[] }) {
    if (guides.length === 0) return null;
    return (
        <group>
            {guides.map((guide, i) => (
                <Line
                    key={i}
                    points={[guide.from, guide.to]}
                    color="#ff9900"
                    lineWidth={1.5}
                    dashed
                    dashSize={0.18}
                    gapSize={0.12}
                    transparent
                    opacity={0.9}
                />
            ))}
        </group>
    );
}

/**
 * Wall Preview Component - Shows wall being drawn
 */
export function WallPreview({ start, end, height, thickness }: WallPreview) {
    if (!start || !end || start.distanceTo(end) < 0.1) return null;

    const length = start.distanceTo(end);
    // Heading in the XY ground plane (Z-up: yaw about +Z)
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

    return (
        <group position={[center.x, center.y, height / 2]} rotation={[0, 0, angle]}>
            {/* Wall mesh preview + wireframe overlay: a single pooled unit box
                scaled live (length X, thickness Y, height Z). Following the
                cursor costs one matrix update per move — no BoxGeometry is
                rebuilt or GC'd while drawing. */}
            <mesh geometry={UNIT_BOX_GEOMETRY} scale={[length, thickness, height]}>
                <meshStandardMaterial
                    color="#4a90d9"
                    transparent
                    opacity={0.5}
                    wireframe={false}
                />
            </mesh>

            <mesh geometry={UNIT_BOX_GEOMETRY} scale={[length, thickness, height]}>
                <meshBasicMaterial color="#ffffff" wireframe />
            </mesh>

            {/* Dimension line below the base */}
            <Line
                points={[
                    [-length / 2, 0, -height / 2 - 0.2],
                    [length / 2, 0, -height / 2 - 0.2],
                ]}
                color="#ffff00"
                lineWidth={2}
            />
        </group>
    );
}

/**
 * Wall Mesh Component - Renders placed walls
 */
export function WallMesh({
    data,
    isSelected,
    isHovered,
    onClick,
}: {
    data: Record<string, unknown>;
    isSelected: boolean;
    isHovered: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}) {
    const length = data.length as number;
    const height = data.height as number;
    const thickness = data.thickness as number;
    const center = data.center as number[];
    const angle = data.angle as number;
    const profile = data.profile as number[][];
    const joinedAtStart = data.joinedAtStart as boolean;
    const joinedAtEnd = data.joinedAtEnd as boolean;

    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#a0a0a0';

    const view = useView();
    const isPlanView = view.viewType === 'plan';

    // Generate geometry based on profile or default box
    const geometry = useMemo(() => {
        if (profile && profile.length > 0) {
            const shape = new THREE.Shape();
            // Profile is in world XY (Z-up): the shape lies flat in the
            // ground plane and ExtrudeGeometry rises along +Z = height.
            shape.moveTo(profile[0][0], profile[0][1]);
            for (let i = 1; i < profile.length; i++) {
                shape.lineTo(profile[i][0], profile[i][1]);
            }
            shape.closePath();

            return new THREE.ExtrudeGeometry(shape, {
                depth: height,
                bevelEnabled: false,
            });
        } else {
            // Local frame: length along X, thickness along Y, height along Z.
            return new THREE.BoxGeometry(length, thickness, height);
        }
    }, [profile, length, height, thickness]);

    const groupProps = profile ? {
        position: new THREE.Vector3(0, 0, 0),
    } : {
        position: new THREE.Vector3(center[0], center[1], center[2]),
        rotation: new THREE.Euler(0, 0, angle),
    };

    let customEdges = null;
    if (profile && isPlanView) {
        const points: THREE.Vector3[] = [];
        const z = height; // Draw edges at the top (local Z)

        // Segment 0: Start Left -> End Left (Side)
        points.push(new THREE.Vector3(profile[0][0], profile[0][1], z));
        points.push(new THREE.Vector3(profile[1][0], profile[1][1], z));

        // Segment 1: End Left -> End Right (End) - Skip if joinedAtEnd
        if (!joinedAtEnd) {
            points.push(new THREE.Vector3(profile[1][0], profile[1][1], z));
            points.push(new THREE.Vector3(profile[2][0], profile[2][1], z));
        }

        // Segment 2: End Right -> Start Right (Side)
        points.push(new THREE.Vector3(profile[2][0], profile[2][1], z));
        points.push(new THREE.Vector3(profile[3][0], profile[3][1], z));

        // Segment 3: Start Right -> Start Left (Start) - Skip if joinedAtStart
        if (!joinedAtStart) {
            points.push(new THREE.Vector3(profile[3][0], profile[3][1], z));
            points.push(new THREE.Vector3(profile[0][0], profile[0][1], z));
        }

        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        customEdges = (
            <lineSegments geometry={lineGeometry}>
                <lineBasicMaterial color={isSelected ? '#00aaff' : 'black'} />
            </lineSegments>
        );
    }

    return (
        <group
            {...groupProps}
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
        >
            {isPlanView ? (
                <HiddenLineWrapper isSelected={isSelected} isHovered={isHovered}>
                    <mesh geometry={geometry} castShadow receiveShadow>
                        <PlanViewMaterial showHatch={true} spacing={5.0} />
                    </mesh>
                    {/* Outline for plan view */}
                    {!customEdges && (
                        <lineSegments>
                            <edgesGeometry args={[geometry]} />
                            <lineBasicMaterial color={isSelected ? '#00aaff' : 'black'} />
                        </lineSegments>
                    )}
                    {customEdges}

                    {/* Layer Separators */}
                    {(() => {
                        const layers = data.layers as { thickness: number }[] || [];
                        if (profile && layers.length > 1 && isPlanView) {
                            const p0 = new THREE.Vector3(profile[0][0], profile[0][1], height);
                            const p1 = new THREE.Vector3(profile[1][0], profile[1][1], height);
                            const p2 = new THREE.Vector3(profile[2][0], profile[2][1], height);
                            const p3 = new THREE.Vector3(profile[3][0], profile[3][1], height);

                            const totalThickness = thickness;
                            let currentThickness = 0;
                            const points: number[] = [];

                            // Vector from Left to Right at Start (P0 -> P3)
                            const vStart = new THREE.Vector3().subVectors(p3, p0);
                            // Vector from Left to Right at End (P1 -> P2)
                            const vEnd = new THREE.Vector3().subVectors(p2, p1);

                            for (let i = 0; i < layers.length - 1; i++) {
                                currentThickness += layers[i].thickness;
                                const ratio = currentThickness / totalThickness;

                                const startPt = p0.clone().add(vStart.clone().multiplyScalar(ratio));
                                const endPt = p1.clone().add(vEnd.clone().multiplyScalar(ratio));

                                points.push(startPt.x, startPt.y, startPt.z);
                                points.push(endPt.x, endPt.y, endPt.z);
                            }

                            if (points.length > 0) {
                                const bufferGeometry = new THREE.BufferGeometry();
                                bufferGeometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
                                return (
                                    <lineSegments geometry={bufferGeometry}>
                                        <lineBasicMaterial color={isSelected ? '#00aaff' : '#444444'} />
                                    </lineSegments>
                                );
                            }
                        }
                        return null;
                    })()}
                </HiddenLineWrapper>
            ) : (
                <mesh geometry={geometry} castShadow receiveShadow>
                    <meshStandardMaterial
                        color={color}
                        roughness={0.8}
                        metalness={0.1}
                    />
                </mesh>
            )}
        </group>
    );
}
